import type { ServiceResa, ZoneResa } from '../../lib/reservationsApi';
import type { FermetureJour, NouvelleFermeture } from '../../lib/reservationsReglagesApi';

/** Tailles de groupe proposées : 1 à 9, puis « 10 et plus » (10 à 49 : au-delà, c'est un événement, à traiter à la main). */
export const TAILLES_SIMPLES = [1, 2, 3, 4, 5, 6, 7, 8, 9];
export const TAILLES_DIX_ET_PLUS = Array.from({ length: 40 }, (_, i) => i + 10);

const enMinutes = (h: string) => Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5));
const enHeure = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** Créneaux d'un service : de l'ouverture à la fermeture (exclue), au rythme du service. */
export const creneauxDuService = (s: Pick<ServiceResa, 'heure_debut' | 'heure_fin' | 'pas_minutes'>): string[] => {
    const pas = s.pas_minutes || 15; const liste: string[] = [];
    for (let m = enMinutes(s.heure_debut); m < enMinutes(s.heure_fin); m += pas) liste.push(enHeure(m));
    return liste;
};

/** Regroupe des créneaux cochés en plages continues : 19:00, 19:15, 20:00 → [19:00–19:30[, [20:00–20:15[. */
export const plagesDeCreneaux = (coches: string[], pas: number): { heure_debut: string; heure_fin: string }[] => {
    const tri = [...new Set(coches)].sort(); const plages: { heure_debut: string; heure_fin: string }[] = [];
    for (const h of tri) {
        const dernier = plages[plages.length - 1];
        if (dernier && dernier.heure_fin === h) dernier.heure_fin = enHeure(enMinutes(h) + pas);
        else plages.push({ heure_debut: h, heure_fin: enHeure(enMinutes(h) + pas) });
    }
    return plages;
};

/** Créneaux couverts par une fermeture de plage (heure_debut incluse, heure_fin exclue). */
export const creneauxDeLaPlage = (f: Pick<FermetureJour, 'heure_debut' | 'heure_fin'>, creneaux: string[]): string[] =>
    f.heure_debut && f.heure_fin ? creneaux.filter((h) => f.heure_debut! <= h && h < f.heure_fin!) : [];

/** « le service du soir » : le nom du service en minuscules, pour les phrases. */
export const nomService = (id: string | null, services: ServiceResa[]): string => (id ? services.find((s) => s.id === id)?.nom.toLowerCase() : '') || 'tous les services';
const nomServiceMaj = (id: string | null, services: ServiceResa[]) => (id ? services.find((s) => s.id === id)?.nom : '') || 'Tous les services';

/** Phrase de la tailles : « 4 couverts », « 3 et 5 couverts », « 10 couverts et plus ». */
export const phraseTailles = (t: number[]): string => {
    const dix = TAILLES_DIX_ET_PLUS.every((x) => t.includes(x));
    const petites = t.filter((x) => x < 10);
    const parts = [...petites.map(String), ...(dix ? ['10 et plus'] : t.filter((x) => x >= 10).map(String))];
    const texte = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} et ${parts[parts.length - 1]}` : parts[0] || '';
    return `${texte} couvert${t.length > 1 || t[0] > 1 ? 's' : ''}`;
};

/** Texte d'une ligne de « Fermé en ce moment » : « 4 couverts · soir · tout le service ». */
export const libelleFermeture = (f: FermetureJour, services: ServiceResa[], zones: ZoneResa[]): string => {
    const sv = nomService(f.resa_service_id, services);
    if (f.type === 'service') return `Service du ${sv} · tout fermé`.replace('du tous les services', 'de tous les services');
    if (f.type === 'creneaux') return `Créneaux ${f.heure_debut}–${f.heure_fin} · ${sv}`;
    if (f.type === 'couverts') return `${phraseTailles(f.tailles)} · ${sv} · ${f.heure_debut ? motsDePlage(f as unknown as NouvelleFermeture, services) : 'tout le service'}`;
    if (f.type === 'zone') return `Zone ${zones.find((z) => z.id === f.zone_id)?.nom || ''} fermée · ${sv}`;
    return f.heure_debut ? `Capacité de ${f.heure_debut} : ${f.valeur} couverts · ${sv}` : `Capacité du jour : ${f.valeur} couverts par créneau · ${sv}`;
};

/** « à partir de 21:00 » quand la plage va jusqu'à la fin du service, sinon « de 20:00 à 21:00 ». */
const motsDePlage = (f: NouvelleFermeture, services: ServiceResa[]): string => {
    if (!f.heure_debut) return '';
    const fin = services.find((s) => s.id === f.resa_service_id)?.heure_fin;
    return fin && f.heure_fin === fin ? `à partir de ${f.heure_debut}` : `de ${f.heure_debut} à ${f.heure_fin}`;
};

/** Phrase du bouton de confirmation : il nomme l'effet (« Fermer les groupes de 2 à partir de 21:00 pour le service du soir »). */
export const phraseConfirmation = (f: NouvelleFermeture, services: ServiceResa[]): string => {
    const sv = nomService(f.resa_service_id ?? null, services);
    const service = sv === 'tous les services' ? 'tous les services' : `le service du ${sv}`;
    const plage = motsDePlage(f, services);
    if (f.type === 'service') return `Fermer ${service}`;
    if (f.type === 'creneaux') return `Fermer les créneaux ${plage} pour ${service}`;
    if (f.type === 'couverts') {
        const t = f.tailles || [];
        const dix = TAILLES_DIX_ET_PLUS.every((x) => t.includes(x));
        const petites = t.filter((x) => x < 10).map(String);
        const liste = [...petites, ...(dix ? ['10 et plus'] : t.filter((x) => x >= 10).map(String))];
        const qui = liste.length > 1 ? `les groupes de ${liste.slice(0, -1).join(', ')} et ${liste[liste.length - 1]}` : `les groupes de ${liste[0]}`;
        return `Fermer ${qui}${plage ? ` ${plage}` : ''} pour ${service}`;
    }
    return 'Enregistrer';
};

/** La fermeture la plus large d'un service pour le jour : « service » > « créneaux » > « couverts ». */
export const serviceFerme = (fermetures: FermetureJour[], serviceId: string): FermetureJour | undefined =>
    fermetures.find((f) => f.type === 'service' && (f.resa_service_id === serviceId || f.resa_service_id === null));

export { nomServiceMaj };

/**
 * Le composant partagé « Checkbox » dessine la coche en blanc sur le fond de la carte : en mode sombre, une case NON cochée montre donc
 * une coche blanche. Composant partagé non modifié (consigne) : ces écrans masquent la coche tant que la case n'est pas cochée.
 */
export const CASES_SOBRES = '[&_.field-check-box_svg]:opacity-0 [&_input:checked+.field-check-box_svg]:opacity-100';

// ---------- Plage horaire choisie dans une fenêtre de fermeture ----------

export type ModePlage = 'service' | 'apartir' | 'entre' | 'cases';
/** Ce que l'on a choisi : tout le service, « à partir de de », « de de à a » (a exclue), ou des créneaux cochés. */
export interface PlageChoisie { mode: ModePlage; de: string; a: string; cases: string[] }

export const plageParDefaut = (creneaux: string[], mode: ModePlage = 'service'): PlageChoisie => ({ mode, de: creneaux[Math.min(creneaux.length - 1, Math.floor(creneaux.length / 2))] || '', a: '', cases: [] });

/**
 * Transforme le choix en plages [heure_debut, heure_fin[ à envoyer au serveur. `null` = tout le service (pas de plage) ;
 * `[]` = choix incomplet (rien de coché, bornes incohérentes).
 */
export const resoudrePlage = (p: PlageChoisie, creneaux: string[], pas: number, finService: string): { heure_debut: string; heure_fin: string }[] | null => {
    if (p.mode === 'service') return null;
    if (p.mode === 'cases') return plagesDeCreneaux(p.cases, pas);
    if (!p.de || !creneaux.includes(p.de)) return [];
    if (p.mode === 'apartir') return [{ heure_debut: p.de, heure_fin: finService }];
    const bornes = [...creneaux.filter((h) => h > p.de), finService];
    const fin = bornes.includes(p.a) ? p.a : bornes[0];
    return fin > p.de ? [{ heure_debut: p.de, heure_fin: fin }] : [];
};

/** « à partir de 21:00 », « de 20:00 à 21:00 », « 3 créneaux » : la plage en mots, pour les boutons et les listes. */
export const motsPlage = (p: PlageChoisie, plages: { heure_debut: string; heure_fin: string }[] | null, finService: string): string => {
    if (plages === null) return 'tout le service';
    if (p.mode === 'apartir' && plages[0]) return `à partir de ${plages[0].heure_debut}`;
    if (p.mode === 'entre' && plages[0]) return `de ${plages[0].heure_debut} à ${plages[0].heure_fin}`;
    const n = p.cases.length;
    void finService;
    return `${n} créneau${n > 1 ? 'x' : ''}`;
};

/** Une fermeture nommée pour un bouton : « tailles 2 à partir de 21:00 », prête à être listée dans le récapitulatif avant l'enregistrement. */
export const phraseFermeture = (f: NouvelleFermeture, services: ServiceResa[]): string => {
    const sv = nomService(f.resa_service_id ?? null, services);
    const service = sv === 'tous les services' ? 'tous les services' : `le service du ${sv}`;
    if (f.type === 'service') return `Fermer ${service}`;
    if (f.type === 'creneaux') return phraseConfirmation(f, services);
    if (f.type === 'couverts') {
        const t = f.tailles || [];
        const dix = TAILLES_DIX_ET_PLUS.every((x) => t.includes(x));
        const liste = [...t.filter((x) => x < 10).map(String), ...(dix ? ['10 et plus'] : t.filter((x) => x >= 10).map(String))];
        const qui = liste.length > 1 ? `les groupes de ${liste.slice(0, -1).join(', ')} et ${liste[liste.length - 1]}` : `les groupes de ${liste[0]}`;
        void qui;
        return phraseConfirmation(f, services);
    }
    if (f.type === 'rythme') return `Capacité du jour : ${f.valeur} couverts par créneau (${service})`;
    return 'Fermer une zone';
};

/** Version COURTE d'une restriction, pour la ligne repliée du livre : « Soir 20:00–21:00 fermé », « Groupes de 2 dès 21:00 ». */
export const libelleCourt = (f: FermetureJour, services: ServiceResa[], zones: ZoneResa[]): string => {
    const sv = f.resa_service_id ? nomServiceMaj(f.resa_service_id, services) : '';
    if (f.type === 'service') return `${sv || 'Tout'} fermé`;
    if (f.type === 'creneaux') return `${sv} ${f.heure_debut}–${f.heure_fin} fermé`.trim();
    if (f.type === 'couverts') {
        const fin = services.find((s) => s.id === f.resa_service_id)?.heure_fin;
        const quand = f.heure_debut ? (fin && f.heure_fin === fin ? ` dès ${f.heure_debut}` : ` ${f.heure_debut}–${f.heure_fin}`) : sv ? ` (${sv.toLowerCase()})` : '';
        return `Groupes de ${phraseTailles(f.tailles).replace(/ couverts?$/, '')}${quand}`;
    }
    if (f.type === 'zone') return `Zone ${zones.find((z) => z.id === f.zone_id)?.nom || ''} fermée`;
    return f.heure_debut ? `Capacité ${f.heure_debut} : ${f.valeur}` : `Capacité du jour : ${f.valeur}`;
};
